// Settings — prototype-faithful (chitos-prototype screens4.jsx SettingsPage).
// Real data only: Brand + Commission are backed by tenant.settings_get/update;
// Staff by staff.*; Domains reuses the existing custom-domain manager; Billing
// summarises tenant.billing.list_my_billing. The prototype's Compliance tab and
// notification-channel toggles have no backend, so they're omitted rather than
// faked (per the standing real-data-or-empty rule).

import { useEffect, useState, type CSSProperties, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { current_language } from '../i18n/index.js';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';
import { apply_accent, current_accent } from '../lib/theme.js';
import { Icon } from '../components/Icon.js';
import { BillingPanel } from './billing.js';
import { SettingsChannelsPage } from './settings-channels.js';

type Locale = 'ta' | 'ml' | 'kn' | 'te' | 'en';
type Role = 'admin' | 'foreman' | 'collector' | 'read_only';

interface NotificationPrefs {
  channels?: { whatsapp?: boolean; email?: boolean; voice?: boolean };
  auto_send_receipt?: boolean;
  auto_reminders_enabled?: boolean;
  daily_send_quota?: number;
  per_member_cooldown_minutes?: number;
}

interface TSettings {
  brand_name_ta: string;
  brand_name_en: string;
  kuladeivam: string | null;
  logo_url: string | null;
  primary_accent: string;
  locale_default: Locale;
  due_day: number;
  foreman_commission_pct: number;
  penalty_pct_per_month: number;
  grace_days: number;
  escalation_threshold_months: number;
  notification_prefs?: NotificationPrefs;
}

interface StaffRec {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: Role;
  is_active: boolean;
  last_login_at: string | Date | null;
}

// Standalone inputs in a .set-row aren't auto-styled (only `.field input` is),
// so they carry the prototype's inline input style.
const INPUT: CSSProperties = {
  font: 'inherit',
  fontSize: 13,
  padding: '7px 10px',
  border: '1px solid var(--line-strong)',
  borderRadius: 6,
  background: 'var(--surface)',
  color: 'var(--ink)',
};

const ACCENTS = [
  { id: 'maroon', hex: '#7B1F2C' },
  { id: 'indigo', hex: '#2D3A7B' },
  { id: 'forest', hex: '#2E5F3D' },
  { id: 'charcoal', hex: '#26201A' },
];

const ROLE_LABEL: Record<Role, { ta: string; en: string }> = {
  admin: { ta: 'நிர்வாகி', en: 'Admin' },
  foreman: { ta: 'ஃபோர்மேன்', en: 'Foreman' },
  collector: { ta: 'களம் கலெக்டர்', en: 'Field Collector' },
  read_only: { ta: 'வாசிக்க மட்டும்', en: 'Read-only' },
};

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '··';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]!.charAt(0) + p[p.length - 1]!.charAt(0)).toUpperCase();
}

const TABS = [
  { id: 'brand', ta: 'வர்த்தக முத்திரை', en: 'Brand & Identity' },
  { id: 'commission', ta: 'கமிஷன் & அபராதம்', en: 'Commission & Penalty' },
  { id: 'notifications', ta: 'அறிவிப்புகள்', en: 'Notifications' },
  { id: 'domains', ta: 'டொமைன்கள்', en: 'Domains' },
  { id: 'staff', ta: 'ஊழியர்கள்', en: 'Staff & Roles' },
  { id: 'billing', ta: 'கட்டணம்', en: 'Billing' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function SettingsPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  // Allow deep-linking a tab via ?tab= (e.g. the nav "Billing" → ?tab=billing).
  const [search_params] = useSearchParams();
  const requested = search_params.get('tab');
  const initial_tab: TabId = TABS.some((tb) => tb.id === requested) ? (requested as TabId) : 'brand';
  const [tab, set_tab] = useState<TabId>(initial_tab);

  const me_q = useQuery({ queryKey: ['me'], queryFn: () => orpc.me.get({}), staleTime: 5 * 60 * 1000 });
  const profile = me_q.data?.profile;
  const tenant_name = profile?.tenant_name ?? 'ChitDesk';
  const scopes = profile?.scopes ?? [];
  const can_write_settings = scopes.includes('settings:write');
  const can_write_staff = scopes.includes('staff:write');
  const subdomain = typeof window !== 'undefined' ? (window.location.hostname.split('.')[0] ?? '') : '';

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('அமைப்புகள்', 'Settings')}</div>
          <div className="h-sub">
            {t('டெனன்ட்', 'Tenant')}: {tenant_name}
            {subdomain ? ` · ${window.location.hostname}` : ''}
          </div>
        </div>
      </div>

      <div className="settings-grid">
        <div className="settings-nav">
          {TABS.map((tb) => (
            <div
              key={tb.id}
              className={'settings-nav-item' + (tab === tb.id ? ' on' : '')}
              onClick={() => set_tab(tb.id)}
            >
              {lang === 'ta' ? tb.ta : tb.en}
            </div>
          ))}
        </div>

        <div>
          {tab === 'brand' && <BrandTab lang={lang} subdomain={subdomain} canWrite={can_write_settings} />}
          {tab === 'commission' && <CommissionTab lang={lang} canWrite={can_write_settings} />}
          {tab === 'notifications' && <NotificationsTab lang={lang} canWrite={can_write_settings} />}
          {tab === 'domains' && <SettingsChannelsPage />}
          {tab === 'staff' && <StaffTab lang={lang} canWrite={can_write_staff} />}
          {tab === 'billing' && <BillingPanel />}
        </div>
      </div>
    </div>
  );
}

// ─── shared settings hook ───────────────────────────────────────────────────

function useTenantSettings(): {
  settings: TSettings | undefined;
  save: (patch: Partial<TSettings>) => void;
  saving: boolean;
  error: unknown;
} {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tenant-settings'], queryFn: () => orpc.tenant.settings_get({}) });
  const m = useMutation({
    mutationFn: (patch: Partial<TSettings>) =>
      orpc.tenant.settings_update({ patch: patch as Record<string, unknown> }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenant-settings'] }),
  });
  return {
    settings: q.data?.settings as TSettings | undefined,
    save: (patch) => m.mutate(patch),
    saving: m.isPending,
    error: m.error,
  };
}

// ─── Brand & Identity ───────────────────────────────────────────────────────

function BrandTab({ lang, subdomain, canWrite }: { lang: string; subdomain: string; canWrite: boolean }): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const { settings, save, saving, error } = useTenantSettings();
  const [form, set_form] = useState<Partial<TSettings>>({});

  useEffect(() => {
    if (settings) {
      set_form({
        brand_name_en: settings.brand_name_en,
        brand_name_ta: settings.brand_name_ta,
        kuladeivam: settings.kuladeivam,
        // Swatch reflects the live accent (cached/forest default), not the
        // legacy server value — clicking re-themes immediately, Save persists.
        primary_accent: current_accent(),
        locale_default: settings.locale_default,
      });
    }
  }, [settings]);

  if (!settings) return <div className="set-card"><div style={{ color: 'var(--ink-3)' }}>Loading…</div></div>;

  const dirty =
    form.brand_name_en !== settings.brand_name_en ||
    form.brand_name_ta !== settings.brand_name_ta ||
    (form.kuladeivam ?? '') !== (settings.kuladeivam ?? '') ||
    form.primary_accent !== settings.primary_accent ||
    form.locale_default !== settings.locale_default;

  return (
    <div className="set-card">
      <div style={{ fontWeight: 600, marginBottom: 10 }}>{t('முத்திரை & அடையாளம்', 'Brand & Identity')}</div>

      <div className="set-row">
        <div>
          <div className="lbl">{t('வர்த்தக பெயர் (ஆங்)', 'Brand name (EN)')}</div>
          <div className="desc">{t('ரசீது & PDF இல் தோன்றும்', 'Appears on receipts & PDFs')}</div>
        </div>
        <input
          style={INPUT}
          value={form.brand_name_en ?? ''}
          disabled={!canWrite}
          onChange={(e) => set_form((f) => ({ ...f, brand_name_en: e.target.value }))}
        />
      </div>

      <div className="set-row">
        <div>
          <div className="lbl">{t('வர்த்தக பெயர் (தமிழ்)', 'Brand name (TA)')}</div>
          <div className="desc">{t('தமிழ் ரசீதுகளில்', 'On Tamil receipts')}</div>
        </div>
        <input
          className="tamil"
          style={INPUT}
          value={form.brand_name_ta ?? ''}
          disabled={!canWrite}
          onChange={(e) => set_form((f) => ({ ...f, brand_name_ta: e.target.value }))}
        />
      </div>

      <div className="set-row">
        <div>
          <div className="lbl">{t('குலதெய்வம்', 'Kuladeivam')}</div>
          <div className="desc">{t('சீட்டு வெளியீடுகளில் காண்பிக்கப்படும்', 'Honoured on chit launches')}</div>
        </div>
        <input
          className="tamil"
          style={INPUT}
          value={form.kuladeivam ?? ''}
          disabled={!canWrite}
          onChange={(e) => set_form((f) => ({ ...f, kuladeivam: e.target.value }))}
        />
      </div>

      <div className="set-row">
        <div>
          <div className="lbl">{t('முதன்மை நிறம்', 'Primary accent')}</div>
          <div className="desc">{t('ரசீது & PDF இல் பயன்படுத்தப்படும்', 'Used on receipts & PDFs')}</div>
        </div>
        <div className="swatch-row">
          {ACCENTS.map((a) => (
            <div
              key={a.id}
              className={'swatch' + ((form.primary_accent ?? '').toLowerCase() === a.hex.toLowerCase() ? ' on' : '')}
              style={{ background: a.hex, cursor: canWrite ? 'pointer' : 'default' }}
              onClick={() => {
                if (!canWrite) return;
                apply_accent(a.hex); // re-theme the live app immediately
                set_form((f) => ({ ...f, primary_accent: a.hex }));
              }}
            />
          ))}
        </div>
      </div>

      <div className="set-row">
        <div>
          <div className="lbl">{t('இயல்பு மொழி', 'Default language')}</div>
          <div className="desc">{t('புதிய ஊழியர்/உறுப்பினர்களுக்கு', 'For new staff & members')}</div>
        </div>
        <div style={{ display: 'inline-flex', gap: 4 }}>
          <button
            className={'btn sm' + (form.locale_default === 'ta' ? ' primary' : '')}
            disabled={!canWrite}
            onClick={() => set_form((f) => ({ ...f, locale_default: 'ta' }))}
          >
            தமிழ்
          </button>
          <button
            className={'btn sm' + (form.locale_default === 'en' ? ' primary' : '')}
            disabled={!canWrite}
            onClick={() => set_form((f) => ({ ...f, locale_default: 'en' }))}
          >
            English
          </button>
        </div>
      </div>

      <div className="set-row">
        <div>
          <div className="lbl">{t('சப்டொமைன்', 'Subdomain')}</div>
          <div className="desc mono">{subdomain ? window.location.hostname : '—'}</div>
        </div>
        <span className="compliance-badge registered">{t('செயலில்', 'Active')}</span>
      </div>

      {error != null && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 8 }}>{friendly_error(error)}</div>}
      {canWrite && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn primary" disabled={!dirty || saving} onClick={() => save(diff_brand(form, settings))}>
            {saving ? '…' : t('சேமி', 'Save changes')}
          </button>
        </div>
      )}
    </div>
  );
}

function diff_brand(form: Partial<TSettings>, base: TSettings): Partial<TSettings> {
  const p: Partial<TSettings> = {};
  if (form.brand_name_en !== base.brand_name_en) p.brand_name_en = form.brand_name_en;
  if (form.brand_name_ta !== base.brand_name_ta) p.brand_name_ta = form.brand_name_ta;
  if ((form.kuladeivam ?? '') !== (base.kuladeivam ?? '')) p.kuladeivam = form.kuladeivam ?? null;
  if (form.primary_accent !== base.primary_accent) p.primary_accent = form.primary_accent;
  if (form.locale_default !== base.locale_default) p.locale_default = form.locale_default;
  return p;
}

// ─── Commission & Penalty ───────────────────────────────────────────────────

function CommissionTab({ lang, canWrite }: { lang: string; canWrite: boolean }): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const { settings, save, saving, error } = useTenantSettings();
  const [form, set_form] = useState<Partial<TSettings>>({});

  useEffect(() => {
    if (settings) {
      set_form({
        foreman_commission_pct: settings.foreman_commission_pct,
        penalty_pct_per_month: settings.penalty_pct_per_month,
        grace_days: settings.grace_days,
      });
    }
  }, [settings]);

  if (!settings) return <div className="set-card"><div style={{ color: 'var(--ink-3)' }}>Loading…</div></div>;

  const num = (v: number | undefined): string => (v == null ? '' : String(v));
  const dirty =
    form.foreman_commission_pct !== settings.foreman_commission_pct ||
    form.penalty_pct_per_month !== settings.penalty_pct_per_month ||
    form.grace_days !== settings.grace_days;

  const rows: Array<{ key: keyof TSettings; ta: string; en: string; desc_ta: string; desc_en: string; suffix: string; step: string }> = [
    { key: 'foreman_commission_pct', ta: 'ஃபோர்மேன் கமிஷன்', en: 'Foreman commission', desc_ta: 'சட்ட உச்சவரம்பு 7%', desc_en: 'Statutory ceiling 7%', suffix: '%', step: '0.5' },
    { key: 'penalty_pct_per_month', ta: 'தாமத அபராதம்', en: 'Late penalty', desc_ta: 'மாதந்தோறும்', desc_en: 'Per month', suffix: '% / mo', step: '0.5' },
    { key: 'grace_days', ta: 'சலுகை காலம்', en: 'Grace period', desc_ta: 'அபராதத்திற்கு முன்', desc_en: 'Before penalty accrual', suffix: t('நாட்கள்', 'days'), step: '1' },
  ];

  return (
    <div className="set-card">
      <div style={{ fontWeight: 600, marginBottom: 10 }}>{t('கமிஷன் & அபராதம்', 'Commission & Penalty')}</div>
      {rows.map((r) => (
        <div className="set-row" key={r.key}>
          <div>
            <div className="lbl">{lang === 'ta' ? r.ta : r.en}</div>
            <div className={'desc ' + (lang === 'ta' ? 'tamil' : '')}>{lang === 'ta' ? r.desc_ta : r.desc_en}</div>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number"
              step={r.step}
              className="mono"
              style={{ ...INPUT, width: 90 }}
              value={num(form[r.key] as number | undefined)}
              disabled={!canWrite}
              onChange={(e) => set_form((f) => ({ ...f, [r.key]: e.target.value === '' ? undefined : Number(e.target.value) }))}
            />
            {r.suffix && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.suffix}</span>}
          </div>
        </div>
      ))}
      {error != null && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 8 }}>{friendly_error(error)}</div>}
      {canWrite && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button className="btn primary" disabled={!dirty || saving} onClick={() => save(diff_commission(form, settings))}>
            {saving ? '…' : t('சேமி', 'Save changes')}
          </button>
        </div>
      )}
    </div>
  );
}

function diff_commission(form: Partial<TSettings>, base: TSettings): Partial<TSettings> {
  const p: Partial<TSettings> = {};
  const keys: (keyof TSettings)[] = ['foreman_commission_pct', 'penalty_pct_per_month', 'grace_days'];
  for (const k of keys) {
    if (form[k] !== undefined && form[k] !== base[k]) (p as Record<string, unknown>)[k] = form[k];
  }
  return p;
}

// ─── Staff & Roles ──────────────────────────────────────────────────────────

function StaffTab({ lang, canWrite }: { lang: string; canWrite: boolean }): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const qc = useQueryClient();
  const [show_invite, set_show_invite] = useState(false);

  const q = useQuery({ queryKey: ['staff'], queryFn: () => orpc.staff.list({ include_inactive: true }) });
  const staff = (q.data?.staff ?? []) as StaffRec[];

  const role_m = useMutation({
    mutationFn: (v: { staff_id: string; role: Role }) => orpc.staff.update_role(v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
  });
  const deact_m = useMutation({
    mutationFn: (staff_id: string) => orpc.staff.deactivate({ staff_id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
  });

  return (
    <div className="set-card">
      <div style={{ fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center' }}>
        {t('ஊழியர்கள் & பாத்திரங்கள்', 'Staff & Roles')}
        {canWrite && (
          <button className="btn primary sm" style={{ marginLeft: 'auto' }} onClick={() => set_show_invite(true)}>
            <Icon.plus /> {t('ஊழியர் சேர்', 'Invite staff')}
          </button>
        )}
      </div>

      {q.isLoading && <div style={{ color: 'var(--ink-3)' }}>Loading…</div>}
      {!q.isLoading && staff.length === 0 && <div style={{ color: 'var(--ink-3)' }}>{t('ஊழியர்கள் இல்லை', 'No staff yet')}</div>}

      {staff.map((s, i) => (
        <div
          key={s.id}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < staff.length - 1 ? '1px dashed var(--line)' : 0, opacity: s.is_active ? 1 : 0.5 }}
        >
          <div className="ava-sm">{initials(s.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>
              {s.name}
              {!s.is_active && <span style={{ fontSize: 10, color: 'var(--ink-3)', marginLeft: 6 }}>· {t('செயலற்றது', 'inactive')}</span>}
            </div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.email ?? s.phone ?? '—'}</div>
          </div>
          {canWrite ? (
            <select
              value={s.role}
              disabled={role_m.isPending}
              onChange={(e) => role_m.mutate({ staff_id: s.id, role: e.target.value as Role })}
              style={{ ...INPUT, width: 150 }}
            >
              {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                <option key={r} value={r}>{lang === 'ta' ? ROLE_LABEL[r].ta : ROLE_LABEL[r].en}</option>
              ))}
            </select>
          ) : (
            <span className="compliance-badge registered">{lang === 'ta' ? ROLE_LABEL[s.role].ta : ROLE_LABEL[s.role].en}</span>
          )}
          {canWrite && s.is_active && (
            <button
              className="btn ghost sm"
              title={t('செயலிழக்கச் செய்', 'Deactivate')}
              disabled={deact_m.isPending}
              onClick={() => {
                if (confirm(t(`${s.name} ஐ செயலிழக்கச் செய்யவா?`, `Deactivate ${s.name}? They will lose access.`))) deact_m.mutate(s.id);
              }}
            >
              <Icon.lock />
            </button>
          )}
        </div>
      ))}

      {show_invite && <InviteModal lang={lang} onClose={() => set_show_invite(false)} />}
    </div>
  );
}

function InviteModal({ lang, onClose }: { lang: string; onClose: () => void }): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const qc = useQueryClient();
  const [name, set_name] = useState('');
  const [phone, set_phone] = useState('+91');
  const [email, set_email] = useState('');
  const [role, set_role] = useState<Role>('collector');

  const m = useMutation({
    mutationFn: () =>
      orpc.staff.invite({ name: name.trim(), phone: phone.trim(), role, ...(email.trim() ? { email: email.trim() } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['staff'] });
      onClose();
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    m.mutate();
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-head">
          <h2>{t('ஊழியர் சேர்', 'Invite staff')}</h2>
          <button className="icon-btn" onClick={onClose}><Icon.close /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="field">
              <label>{t('பெயர்', 'Name')}</label>
              <input value={name} required onChange={(e) => set_name(e.target.value)} />
            </div>
            <div className="field">
              <label>{t('கைபேசி (+91…)', 'Phone (+91…)')}</label>
              <input className="mono" value={phone} required pattern="\+[1-9][0-9]{6,14}" onChange={(e) => set_phone(e.target.value)} />
            </div>
            <div className="field">
              <label>{t('மின்னஞ்சல் (விருப்பம்)', 'Email (optional)')}</label>
              <input type="email" value={email} onChange={(e) => set_email(e.target.value)} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>{t('பாத்திரம்', 'Role')}</label>
              <select value={role} onChange={(e) => set_role(e.target.value as Role)}>
                {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
                  <option key={r} value={r}>{lang === 'ta' ? ROLE_LABEL[r].ta : ROLE_LABEL[r].en}</option>
                ))}
              </select>
            </div>
            {m.error != null && <div style={{ color: 'var(--bad)', fontSize: 12, marginTop: 10 }}>{friendly_error(m.error)}</div>}
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>{t('ரத்து', 'Cancel')}</button>
            <button type="submit" className="btn primary" disabled={m.isPending}>
              {m.isPending ? '…' : t('அழைப்பு அனுப்பு', 'Send invite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Billing is rendered by <BillingPanel> (from ./billing.js) — moved out of a
// standalone /billing page into this Settings tab.

// ─── Notifications tab ─────────────────────────────────────────────────────
//
// Surfaces tenant.notification_prefs (channel kill switches, auto-receipt
// toggle, daily quota, cooldown). The shape on disk is sparse — every
// field optional — so the form merges the loaded prefs with the schema
// defaults and writes the whole sub-doc back on save.

const PREFS_DEFAULTS: Required<NotificationPrefs> & {
  channels: { whatsapp: boolean; email: boolean; voice: boolean };
} = {
  channels: { whatsapp: true, email: true, voice: true },
  auto_send_receipt: true,
  auto_reminders_enabled: true,
  daily_send_quota: 2000,
  per_member_cooldown_minutes: 60,
};

function NotificationsTab({
  lang,
  canWrite,
}: {
  lang: string;
  canWrite: boolean;
}): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const { settings, save, saving, error } = useTenantSettings();
  const [prefs, set_prefs] = useState<NotificationPrefs | null>(null);

  // Hydrate the editable form from the loaded settings on first render.
  // Subsequent toggles drive `prefs` directly so the form is live-editable.
  const current: typeof PREFS_DEFAULTS = {
    channels: {
      whatsapp: prefs?.channels?.whatsapp ?? settings?.notification_prefs?.channels?.whatsapp ?? PREFS_DEFAULTS.channels.whatsapp,
      email: prefs?.channels?.email ?? settings?.notification_prefs?.channels?.email ?? PREFS_DEFAULTS.channels.email,
      voice: prefs?.channels?.voice ?? settings?.notification_prefs?.channels?.voice ?? PREFS_DEFAULTS.channels.voice,
    },
    auto_send_receipt:
      prefs?.auto_send_receipt ?? settings?.notification_prefs?.auto_send_receipt ?? PREFS_DEFAULTS.auto_send_receipt,
    auto_reminders_enabled:
      prefs?.auto_reminders_enabled ?? settings?.notification_prefs?.auto_reminders_enabled ?? PREFS_DEFAULTS.auto_reminders_enabled,
    daily_send_quota:
      prefs?.daily_send_quota ?? settings?.notification_prefs?.daily_send_quota ?? PREFS_DEFAULTS.daily_send_quota,
    per_member_cooldown_minutes:
      prefs?.per_member_cooldown_minutes ?? settings?.notification_prefs?.per_member_cooldown_minutes ?? PREFS_DEFAULTS.per_member_cooldown_minutes,
  };

  function patch(p: Partial<NotificationPrefs>): void {
    set_prefs((existing) => ({ ...current, ...(existing ?? {}), ...p }));
  }

  function save_all(): void {
    save({ notification_prefs: current });
    set_prefs(null);
  }

  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="card-title">{t('அறிவிப்பு அமைப்புகள்', 'Notification settings')}</div>
      <div className="card-sub" style={{ marginBottom: 16 }}>
        {t(
          'அலைவரிசை, தானியங்கி ரசீது, தினசரி வரம்பு — அனைத்தும் இங்கே.',
          'Channels, auto-receipt, daily quota — all here.',
        )}
      </div>

      {/* Channel kill switches */}
      <section style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
          {t('அலைவரிசை', 'Channels')}
        </div>
        {(['whatsapp', 'email', 'voice'] as const).map((c) => (
          <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={current.channels[c]}
              disabled={!canWrite}
              onChange={(e) =>
                patch({
                  channels: { ...current.channels, [c]: e.target.checked },
                })
              }
            />
            <span style={{ textTransform: 'capitalize' }}>
              {c === 'whatsapp' ? 'WhatsApp' : c === 'email' ? t('மின்னஞ்சல்', 'Email') : t('குரல்', 'Voice')}
            </span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {current.channels[c] ? t('— செயலில்', '— enabled') : t('— முடக்கப்பட்டது', '— disabled')}
            </span>
          </label>
        ))}
      </section>

      {/* Auto-receipt + auto-reminders */}
      <section style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
          {t('தானியங்கி', 'Automated flows')}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={current.auto_send_receipt}
            disabled={!canWrite}
            onChange={(e) => patch({ auto_send_receipt: e.target.checked })}
          />
          {t('கட்டணம் வசூலித்த பின்னர் ரசீது அனுப்பு', 'Send a receipt after collecting payment')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
          <input
            type="checkbox"
            checked={current.auto_reminders_enabled}
            disabled={!canWrite}
            onChange={(e) => patch({ auto_reminders_enabled: e.target.checked })}
          />
          {t('தானியங்கி நினைவூட்டல்கள் இயக்கு', 'Run the automatic reminder cron')}
        </label>
      </section>

      {/* Quotas */}
      <section style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
          {t('வரம்புகள்', 'Limits')}
        </div>
        <label style={{ display: 'block', fontSize: 13, marginBottom: 8 }}>
          {t('தினசரி அனுப்பு வரம்பு', 'Daily send quota')}
          <input
            type="number"
            min={0}
            max={20000}
            value={current.daily_send_quota}
            disabled={!canWrite}
            onChange={(e) => patch({ daily_send_quota: Math.max(0, Number(e.target.value)) })}
            style={{ display: 'block', width: 120, padding: '4px 6px', marginTop: 4, border: '1px solid var(--line)', borderRadius: 4 }}
          />
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {t('0 = வரம்பின்றி', '0 = unlimited')}
          </span>
        </label>
        <label style={{ display: 'block', fontSize: 13 }}>
          {t('உறுப்பினர் இடைவெளி (நிமிடம்)', 'Per-member cooldown (minutes)')}
          <input
            type="number"
            min={0}
            max={1440}
            value={current.per_member_cooldown_minutes}
            disabled={!canWrite}
            onChange={(e) => patch({ per_member_cooldown_minutes: Math.max(0, Number(e.target.value)) })}
            style={{ display: 'block', width: 120, padding: '4px 6px', marginTop: 4, border: '1px solid var(--line)', borderRadius: 4 }}
          />
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {t('அதே நோக்கத்தை அதே நபருக்கு X நிமிடத்திற்குள் அனுப்ப முடியாது', 'Same intent → same member blocked within X minutes')}
          </span>
        </label>
      </section>

      {error ? (
        <div style={{ fontSize: 12, color: 'var(--bad)', marginBottom: 10 }}>
          {friendly_error(error)}
        </div>
      ) : null}

      <button
        type="button"
        className="btn primary"
        disabled={!canWrite || saving || !prefs}
        onClick={save_all}
      >
        {saving ? '…' : t('சேமி', 'Save')}
      </button>

      <hr style={{ margin: '24px 0', border: 0, borderTop: '1px solid var(--line)' }} />

      <OptedOutPanel lang={lang} canWrite={canWrite} />
    </div>
  );
}

// "Members opted out" — every subscriber with any do_not_contact flag set.
// One "Opt in" button per channel per row; flip happens via
// notify.member_prefs_update. The list refetches after every flip so the
// row drops from the table once all channels are restored.
function OptedOutPanel({ lang, canWrite }: { lang: string; canWrite: boolean }): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['opted-out'],
    queryFn: () => orpc.whatsapp.list_opted_out({}),
  });
  const list = q.data?.subscribers ?? [];
  const m = useMutation({
    mutationFn: (input: {
      subscriber_id: string;
      channel: 'whatsapp' | 'email' | 'voice';
    }) =>
      orpc.notify.member_prefs_update({
        subscriber_id: input.subscriber_id,
        do_not_contact: { [input.channel]: false },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['opted-out'] }),
  });

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {t('தவிர்க்கப்பட்ட உறுப்பினர்கள்', 'Members opted out')}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 10 }}>
        {t(
          '"stop" அனுப்பியவர்கள் அல்லது மின்னஞ்சல் தவிர்த்தவர்கள் இங்கே தோன்றுவார்கள்.',
          'Members who texted "stop" or unsubscribed from email appear here. Restore any channel below.',
        )}
      </div>
      {q.isLoading && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Loading…</div>}
      {q.data && list.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {t('யாரும் இல்லை', 'Nobody is opted out — good signal.')}
        </div>
      )}
      {list.length > 0 && (
        <table style={{ width: '100%', fontSize: 12 }}>
          <thead style={{ background: 'var(--bg-elev)', color: 'var(--ink-3)' }}>
            <tr>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>{t('உறுப்பினர்', 'Member')}</th>
              <th style={{ textAlign: 'center', padding: '6px 8px' }}>WhatsApp</th>
              <th style={{ textAlign: 'center', padding: '6px 8px' }}>{t('மின்னஞ்சல்', 'Email')}</th>
              <th style={{ textAlign: 'center', padding: '6px 8px' }}>{t('குரல்', 'Voice')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={{ padding: '6px 8px' }}>
                  <div className={lang === 'ta' ? 'tamil' : ''}>{lang === 'ta' ? s.name_ta : s.name_en}</div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>{s.phone}</div>
                </td>
                {(['whatsapp', 'email', 'voice'] as const).map((c) => (
                  <td key={c} style={{ textAlign: 'center', padding: '6px 8px' }}>
                    {s.do_not_contact[c] ? (
                      <button
                        type="button"
                        disabled={!canWrite || m.isPending}
                        onClick={() => m.mutate({ subscriber_id: s.id, channel: c })}
                        className="btn sm"
                      >
                        {t('மீட்டமை', 'Opt in')}
                      </button>
                    ) : (
                      <span style={{ color: 'var(--good)' }}>✓</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
